/* eslint-disable no-empty */
/**
 * Phase A R3 — useRecorder hook.
 *
 * Records the DJ engine's master output to a webm/opus blob via
 * MediaRecorder. The blob can be downloaded locally or uploaded to
 * the room's recordings endpoint.
 *
 * The recorder taps the same MediaStream that LiveKit publishes
 * (`engine.broadcastDest.stream`) so what gets recorded is *exactly*
 * what listeners hear.
 *
 * Usage:
 *   const rec = useRecorder({ engineRef });
 *   rec.start() / rec.stop()
 *   rec.lastBlob → Blob | null
 *   rec.download() → triggers browser download
 *   rec.uploadToRoom(roomId) → POSTs to /api/rooms/{id}/recordings
 */
import { useCallback, useEffect, useRef, useState } from "react";

const MIME = (() => {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return "";
})();

export function useRecorder({ engineRef }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [lastBlob, setLastBlob] = useState(null);
  const [lastFilename, setLastFilename] = useState("");
  const [error, setError] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(0);

  const start = useCallback(() => {
    setError("");
    const eng = engineRef.current;
    const stream = eng?.broadcastDest?.stream;
    if (!stream) { setError("Engine not ready"); return false; }
    if (!MIME) { setError("MediaRecorder unsupported"); return false; }
    try {
      const r = new MediaRecorder(stream, { mimeType: MIME, audioBitsPerSecond: 192000 });
      chunksRef.current = [];
      r.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      r.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: MIME });
        const stamp = new Date().toISOString().replace(/[:.]/g, "-").replace("T", "_").slice(0, 19);
        setLastBlob(blob);
        setLastFilename(`mix-${stamp}.webm`);
        recorderRef.current = null;
      };
      // Phase A R9 D — 5s timeslice for long-set robustness. At 192kbps
      // stereo, each chunk is ~120KB; a 2-hour set produces ~1440 chunks
      // (~172MB) which is well within browser memory. We dispatch a
      // `sr-toast` start announcement so the DJ sees confirmation —
      // the previous build was silent on start which is why users
      // couldn't tell if recording had actually begun.
      r.start(5000);
      recorderRef.current = r;
      startedAtRef.current = performance.now();
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((performance.now() - startedAtRef.current) / 1000));
      }, 500);
      setRecording(true);
      try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "success", msg: "Recording started — file will save when you press Stop." } })); } catch { /* */ }
      // eslint-disable-next-line no-console
      console.log(`[recorder] start mime=${MIME} timeslice=5000ms streamTracks=${stream.getTracks().length}`);
      return true;
    } catch (e) {
      setError(String(e));
      return false;
    }
  }, [engineRef]);

  const stop = useCallback(() => {
    if (!recorderRef.current) return;
    try { recorderRef.current.stop(); } catch {}
    clearInterval(tickRef.current);
    setRecording(false);
    // eslint-disable-next-line no-console
    console.log(`[recorder] stop after ${Math.floor((performance.now() - startedAtRef.current) / 1000)}s`);
    try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "info", msg: "Recording stopped — click Save mix to download." } })); } catch { /* */ }
  }, []);

  useEffect(() => () => { try { clearInterval(tickRef.current); } catch {} }, []);

  const download = useCallback(() => {
    if (!lastBlob) return;
    const url = URL.createObjectURL(lastBlob);
    const a = document.createElement("a");
    a.href = url; a.download = lastFilename || "mix.webm";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }, [lastBlob, lastFilename]);

  const uploadToRoom = useCallback(async (roomId) => {
    if (!lastBlob || !roomId) return null;
    const fd = new FormData();
    fd.append("file", lastBlob, lastFilename || "mix.webm");
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const res = await fetch(`${base}/api/rooms/${roomId}/recordings`, {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!res.ok) { setError(`upload failed (${res.status})`); return null; }
    return await res.json();
  }, [lastBlob, lastFilename]);

  return {
    recording, elapsed, lastBlob, lastFilename, error,
    start, stop, download, uploadToRoom,
    mimeSupported: !!MIME,
  };
}
